import jsQR from 'jsqr';

/**
 * Decodifica el CONTENIDO de un código QR a partir de una imagen.
 *
 * Se usa para el flujo de "subir imagen desde archivos" del WebView nativo:
 * el nativo entrega la IMAGEN (base64), no el contenido del QR. El backend
 * /leerQr espera el contenido decodificado (igual que el flujo de cámara),
 * por eso hay que analizar la imagen acá y extraer el string del QR.
 *
 * Usa el mismo motor (jsQR) que react-qr-reader y replica su paso clave:
 * escalar la imagen a `resolution` px (dimensión mayor, manteniendo aspect
 * ratio) antes de decodificar. Es CRÍTICO en el WebView de Android: una foto
 * de alta resolución a tamaño natural desborda el canvas / getImageData y jsQR
 * no la lee. Escalar a 600px lo hace robusto (y más rápido).
 *
 * @param {string} imageSrc - data-URI ('data:image/...;base64,...') o base64 crudo.
 * @param {number} [resolution=600] - dimensión mayor objetivo (igual que react-qr-reader).
 * @returns {Promise<string|null>} contenido del QR, o null si no se pudo leer.
 */
const decodeQrFromImage = (imageSrc, resolution = 600) => new Promise((resolve) => {
  if (typeof imageSrc !== 'string' || imageSrc.trim() === '') {
    resolve(null);
    return;
  }

  // Normalizar a data-URI para poder cargarlo en un <img>.
  const src = imageSrc.startsWith('data:')
    ? imageSrc
    : `data:image/jpg;base64,${imageSrc}`;

  const img = new Image();

  img.onload = () => {
    try {
      const naturalWidth = img.naturalWidth || img.width;
      const naturalHeight = img.naturalHeight || img.height;

      if (naturalWidth === 0 || naturalHeight === 0) {
        resolve(null);
        return;
      }

      // Escalar a `resolution` px la dimensión mayor, manteniendo aspect ratio
      // (idéntico a react-qr-reader.check() en legacyMode).
      const greatestSize = Math.max(naturalWidth, naturalHeight);
      const ratio = resolution / greatestSize;
      const width = Math.max(1, Math.floor(naturalWidth * ratio));
      const height = Math.max(1, Math.floor(naturalHeight * ratio));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      const imageData = ctx.getImageData(0, 0, width, height);

      // Mismas opciones que el worker de react-qr-reader.
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'attemptBoth'
      });

      resolve(code && code.data ? code.data : null);
    } catch (err) {
      resolve(null);
    }
  };

  img.onerror = () => resolve(null);

  img.src = src;
});

export default decodeQrFromImage;
